import { Box, IconNew, ModBox, TextNew } from '@jfs/ui-new'
import { useIsFocused } from '@react-navigation/native'
import { useCallback, useEffect, useState } from 'react'
import Tabbar, { TabbarType } from '../../../components/Tabbar'
import { useLazyApiData, useStores } from '../../../data/store'
import { bulkEntityStringConstants } from '../../../utils/constants'
import EmiFileUpload from '../../EmiFileUpload'
import { FileDataContent } from './FileDataContent'
import ReviewUploadedFile from './ReviewUploadedFile'
import TableDataSubmission from './TableDataSubmission'
import { UploadedFileDetails } from './UploadedFileDetails'
import { UploadedFileHistory } from './UploadedFileHistory'
import { styles } from './styles'

const defaultTitles = [{ title: 'Bulk Upload' }, { title: 'History' }]

interface HeaderInterface {
  onSelected: (selectedIndex: number) => void
  selectedTabIndex: number
  isFileContentVisible: boolean
  fileName?: string
  onBackPress?: () => void
  type: 'bulkUpload' | 'cardSection' | 'tid' | 'bulkConfig' | 'bulkCard' | 'bulkVpa' | 'bulkStore' | 'bulkTerminal'
}

const Header = ({
  onSelected,
  selectedTabIndex,
  isFileContentVisible,
  fileName,
  onBackPress,
  type,
}: HeaderInterface) => {
  const isBulkUploadTab = selectedTabIndex === 1
  const getHeaderTitle = () => {
    if (isFileContentVisible && isBulkUploadTab) {
      return `${bulkEntityStringConstants[type].headerTitle}${fileName}`
    }
    return bulkEntityStringConstants[type].createBulkEntity
  }

  return (
    <Box {...styles.header(isFileContentVisible)}>
      <ModBox show={isFileContentVisible} {...styles.backButtonContainer} onPress={onBackPress}>
        <IconNew id="ARROW_BACK" color={'BLUE'} size={22} />
      </ModBox>
      <Box maxWidth={'40%'}>
        <TextNew title={getHeaderTitle()} {...styles.headerStyle} numberOfLines={1} />
      </Box>
      <ModBox {...styles.tabbarView} show={!isFileContentVisible}>
        <Tabbar
          titles={defaultTitles}
          default={selectedTabIndex}
          type={TabbarType.bulkEntity}
          onSelected={onSelected}
        />
      </ModBox>
    </Box>
  )
}

interface CreateBulkEntities {
  type: 'bulkUpload' | 'tid' | 'bulkConfig' | 'bulkCard' | 'bulkVpa' | 'bulkStore' | 'bulkTerminal'
}

const CreateBulkEntities = ({ type = 'bulkUpload' }: CreateBulkEntities) => {
  const {
    updateUploadedExcelFile,
    getUploadedExcelFileData,
    updateFileName,
    returnExcelFileName,
    submitFileForBulkEntityCreation,
    uploadedFile,
  } = useStores((root) => ({
    updateUploadedExcelFile: root.bulkEntity.updateUploadedExcelFile,
    getUploadedExcelFileData: root.bulkEntity.getUploadedExcelFileData,
    updateFileName: root.bulkEntity.updateFileName,
    returnExcelFileName: root.bulkEntity.returnExcelFileName,
    submitFileForBulkEntityCreation: root.bulkEntity.submitFileForBulkEntityCreation,
    uploadedFile: root.bulkEntity.uploadedFile,
  }))

  const [uploadBulkEntityFile, { state }] = useLazyApiData(submitFileForBulkEntityCreation)

  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [isReviewSelected, toggleReviewSelected] = useState(false)
  const [uploading, toggleUploading] = useState(false)
  const [isSubmitted, toggleSubmission] = useState(false)
  const [isFileContentVisible, toggleFileContentVisibility] = useState(false)

  const isFocused = useIsFocused()

  useEffect(() => {
    setSelectedTabIndex(0)
  }, [isFocused])
  useEffect(() => {
    if (state === 'pending') {
      toggleUploading(true)
    } else if (state === 'fulfilled') {
      toggleUploading(false)
      toggleSubmission(true)
      toggleReviewSelected(false)
    } else if (state === 'rejected') {
      toggleUploading(false)
    }
  }, [state])

  const isBulkUploadType =
    type === 'bulkUpload' ||
    type === 'tid' ||
    type === 'bulkConfig' ||
    type === 'bulkCard' ||
    type === 'bulkVpa' ||
    type === 'bulkStore' ||
    type === 'bulkTerminal' ||
    type === 'MCC_EXPORT' ||
    type === 'IFSC_EXPORT' ||
    type === 'PINCODE_EXPORT' ||
    type === 'PROGRAM_CATEGORY_EXPORT' ||
    type === 'BULK_STORE_UPDATE' ||
    type === 'BULK_DEVICES' ||
    type === 'BULK_TERMINAL_UPDATE' ||
    type === 'ADD_MERCHANT_MAPPING' ||
    type === 'BULK_KEY_EXCHANGE_UPLOAD' ||
    type === 'APPROVE_MERCHANT_MAPPING' ||
    type === 'BATCH_MAKER' ||
    type === 'BATCH_CHECKER' ||
    type === 'BULK_KEY_EXCHANGE_UPLOAD' ||
    type === 'BULK_USER_MANAGEMENT' ||
    type === 'CHECKER_1_BULK_CLAIM' ||
    type === 'CHECKER_2_BULK_CLAIM' ||
    type === 'CHECKER_1_BULK_ACTION' ||
    type === 'CHECKER_2_BULK_ACTION'

  const isBulkUploadTab = selectedTabIndex === 0

  const onSelected = (selectedIndex: number) => {
    setSelectedTabIndex(selectedIndex)
  }

  const onReviewPressed = (val: boolean) => {
    toggleReviewSelected(val)
  }

  const onSubmitPress = useCallback(() => {
    uploadBulkEntityFile(uploadedFile?.id, type)
  }, [type, uploadBulkEntityFile, uploadedFile?.id])

  const onUploadNewFilePress = useCallback(() => {
    setSelectedTabIndex(0)
    toggleReviewSelected(false)
    toggleSubmission(false)
    toggleUploading(false)
    updateFileName('') //CLEAR File name in store.
    updateUploadedExcelFile([]) //CLEAR Uploaded file data array store.
    toggleFileContentVisibility(false)
  }, [updateFileName, updateUploadedExcelFile])

  const onDonePress = useCallback(() => {
    setSelectedTabIndex(0)
    toggleReviewSelected(false)
    toggleSubmission(false)
    toggleUploading(false)
    updateFileName('') //CLEAR File name in store.
    updateUploadedExcelFile([]) //CLEAR Uploaded file data array store.
    toggleFileContentVisibility(false)
  }, [updateFileName, updateUploadedExcelFile])

  const onBackPress = useCallback(() => {
    if (type === 'bulkUpload') {
      toggleFileContentVisibility(false)
    }
  }, [type])

  const onShowFileContent = () => {
    toggleFileContentVisibility((prev) => !prev)
  }

  return (
    <Box {...styles.container}>
      <Header
        onSelected={onSelected}
        selectedTabIndex={selectedTabIndex}
        fileName={returnExcelFileName()}
        isFileContentVisible={isFileContentVisible}
        onBackPress={onBackPress}
        type={type}
      />

      <ModBox show={isBulkUploadType} {...styles.mainView}>
        <ModBox {...styles.innerContainer} show={isBulkUploadTab && !isReviewSelected && !isSubmitted}>
          <EmiFileUpload
            type={type}
            title={bulkEntityStringConstants[type].fileUploadTitle}
            dropText={bulkEntityStringConstants[type].dropTextTitle}
            onReviewPressed={onReviewPressed}
            updateUploadedExcelFile={updateUploadedExcelFile}
            updateFileName={updateFileName}
            fileType=".xlsx"
          />
        </ModBox>
        <ModBox show={isBulkUploadTab && isReviewSelected && !uploading} {...styles.innerContainer}>
          {/* Review the uploaded file component  */}
          <ReviewUploadedFile
            onSubmitPress={onSubmitPress}
            onUploadNewFilePress={onUploadNewFilePress}
            getUploadedExcelFileData={getUploadedExcelFileData}
            returnExcelFileName={returnExcelFileName()}
            type={type}
          />
        </ModBox>
        {/* Uploading salt brewer component  */}
        <ModBox show={isBulkUploadTab && isReviewSelected && uploading && !isSubmitted} {...styles.innerContainer}>
          <TableDataSubmission
            type={type}
            toggleUploading={toggleUploading}
            toggleSubmission={toggleSubmission}
            toggleReviewSelected={toggleReviewSelected}
          />
        </ModBox>
        <ModBox show={isBulkUploadTab && !uploading && isSubmitted} {...styles.innerContainer}>
          {/* Review the details of uploaded file component  */}
          <UploadedFileDetails onSubmitPress={onDonePress} onUploadNewFilePress={onUploadNewFilePress} type={type} />
        </ModBox>
        <ModBox show={!isBulkUploadTab && !isFileContentVisible} {...styles.innerContainer}>
          <UploadedFileHistory
            type={type}
            onViewPress={onShowFileContent}
            onDone={onDonePress}
            onUploadNewFile={onUploadNewFilePress}
          />
        </ModBox>
        <ModBox show={!isBulkUploadTab && isFileContentVisible} {...styles.innerContainer}>
          <FileDataContent type={type} />
        </ModBox>
      </ModBox>
    </Box>
  )
}
export default CreateBulkEntities
